import {
  useFindMany,
  useGlobalAction,
  useMaybeFindFirst,
} from "@gadgetinc/react";
import clsx from "clsx";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../../api";
import { OverflowFrame, PageHeading } from "../../externalComponents";
import { FallbackPage } from "../../externalComponents/FallbackPage";
import LoadingSpinner from "../../externalComponents/LoadingSpinner";
import { useIsMobile } from "../../externalComponents/useIsMobile";
import { CustomerContext } from "../../providers";
import {
  ContactInformations,
  ContactKPI,
  ContactLastInteractions,
  DialogManageConsent,
} from "./shared";

const ContactPage = () => {
  const { customer, integrations } = useContext(CustomerContext);
  const location = useLocation();
  const contactId = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [isConsentDialogOpen, setIsConsentDialogOpen] = useState(false);

  const alreadyFetchingContactStats = useRef(false);
  const retries = useRef(0);
  const MAX_RETRIES = 2;

  const [getContactStatsResult, getContactStats] = useGlobalAction(
    api.getContactStats,
  );

  const fetchContactStats = async () => {
    try {
      const { error } = await getContactStats({ contactId });

      if (error) {
        throw error;
      }
    } catch (error) {
      if (retries.current < MAX_RETRIES) {
        retries.current += 1;
        console.error(
          `getContactStats échec tentative ${retries.current}, nouvelle tentative...`,
        );
        fetchContactStats();
      } else {
        console.error(
          `getContactStats a échoué après ${MAX_RETRIES} tentatives`,
          error,
        );
      }
    }
  };

  useEffect(() => {
    if (contactId && !alreadyFetchingContactStats.current) {
      alreadyFetchingContactStats.current = true;
      fetchContactStats();
    }
  }, [contactId, getContactStats]);

  const [{ data, fetching }, refreshContact] = useMaybeFindFirst(api.contact, {
    filter: {
      id: { equals: contactId },
      customerId: { equals: customer.id },
    },
    select: {
      id: true,
      firstName: true,
      lastName: true,
      createdAt: true,
      updatedAt: true,
      hasOptIn: true,
      optinAt: true,
      optInSource: { name: true },
      optoutScenario: { name: true },
      optoutAt: true,
      userPhoneNumber: true,
      birthday: true,
      externalId: true,
      shopifyCustomer: {
        firstName: true,
        lastName: true,
      },
      whatsappName: true,
      conversations: {
        edges: {
          node: {
            id: true,
            isArchived: true,
          },
        },
      },
      customProperties: true,
    },
    sort: {
      updatedAt: "Descending",
    },
  });

  const [tagsResult] = useFindMany(api.contactTag, {
    filter: {
      contactId: { equals: contactId },
    },
    select: {
      id: true,
      tag: {
        name: true,
      },
    },
  });

  const [segmentsResult] = useFindMany(api.segmentMembership, {
    first: 250,
    filter: {
      contactId: { equals: contactId },
    },
    select: {
      id: true,
      segment: {
        id: true,
        name: true,
      },
    },
  });

  const { title, lastActiveConversationId, customProperties } = useMemo(() => {
    if (!data) {
      return {};
    }
    const {
      whatsappName,
      shopifyCustomer,
      firstName,
      lastName,
      conversations,
      customProperties,
    } = data;
    const _title =
      whatsappName ??
      (shopifyCustomer?.firstName || firstName || "") +
        " " +
        (shopifyCustomer?.lastName || lastName || "");

    const lastActiveConversation = conversations?.edges?.find(
      ({ node }) => !node.isArchived,
    );
    const _lastActiveConversationId = lastActiveConversation?.node.id;
    return {
      title: _title,
      lastActiveConversationId: _lastActiveConversationId,
      customProperties,
    };
  }, [data]);

  const ContactInformationsCard = (
    <ContactInformations
      data={data}
      tagsResult={tagsResult}
      segmentsResult={segmentsResult}
      customProperties={customProperties}
      onManageConsent={() => setIsConsentDialogOpen(true)}
    />
  );

  return !data && !fetching ? (
    <FallbackPage />
  ) : (
    <>
      <PageHeading
        title={title}
        actionsBelow={isMobile}
        isPrimaryDisabled={!lastActiveConversationId}
        primaryAction="See conversation"
        handlePrimaryAction={() =>
          navigate(`/conversations/${lastActiveConversationId}`)
        }
      />

      {fetching && <LoadingSpinner />}

      <OverflowFrame>
        <div className="flex flex-col md:flex-row md:gap-5 w-full p-5 md:h-full">
          {isMobile && (
            <div className="border rounded-xl p-5 w-full">
              {fetching ? <LoadingSpinner /> : ContactInformationsCard}
            </div>
          )}
          <div className="flex flex-col w-full">
            <div
              className={clsx(
                "md:grid  gap-5 sm:grid-cols-3",
                integrations.shopify &&
                  "md:grid-rows-2 min-[h-300px] md:max-h-[300px]",
              )}
            >
              <ContactKPI
                label="Campaigns and Automations received"
                data={getContactStatsResult.data}
                fetching={getContactStatsResult.fetching}
                keyRate="delivered"
              />

              <ContactKPI
                label="Open rate"
                data={getContactStatsResult.data}
                fetching={getContactStatsResult.fetching}
                keyRate="openRate"
              />

              <ContactKPI
                label="Failed rate"
                data={getContactStatsResult.data}
                fetching={getContactStatsResult.fetching}
                keyRate="failedRate"
              />

              {integrations.shopify && (
                <>
                  <ContactKPI
                    label="Attributed sales"
                    data={getContactStatsResult.data}
                    fetching={getContactStatsResult.fetching}
                    keyRate="sales"
                  />

                  <ContactKPI
                    label="Attributed turnover"
                    data={getContactStatsResult.data}
                    fetching={getContactStatsResult.fetching}
                    keyRate="turnover"
                  />

                  <ContactKPI
                    label="Conversion rate"
                    data={getContactStatsResult.data}
                    fetching={getContactStatsResult.fetching}
                    keyRate="conversionRate"
                  />
                </>
              )}
            </div>
            <div
              className={clsx(
                "border rounded-xl py-5 md:overflow-y-auto h-full",
                !isMobile &&
                  "scrollbar mt-5 max-lg:h-[calc(100vh-12em)] md:h-[calc(100vh-7em)]",
              )}
            >
              <ContactLastInteractions
                contactId={contactId}
                customerId={customer.id}
                isMobile={isMobile}
              />
            </div>
          </div>
          {!isMobile && (
            <div className="border rounded-xl p-5 min-w-[320px] w-[50%] max-w-[500px] overflow-y-auto">
              {fetching ? <LoadingSpinner /> : ContactInformationsCard}
            </div>
          )}
        </div>
      </OverflowFrame>

      <DialogManageConsent
        currentConsent={data}
        contactNumber={data?.userPhoneNumber}
        isModalOpen={isConsentDialogOpen}
        onClose={() => setIsConsentDialogOpen(false)}
        onSuccess={() => refreshContact()}
      />
    </>
  );
};
export default ContactPage;
